import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { buildCsv } from '../lib/csv';
import { downloadText } from '../lib/download';

interface Currency {
  _id: string;
  name: string;
  code: string;
  symbol?: string;
  decimalPlaces?: number;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
  fractionalUnit?: string;
  srNo?: string;
}

const CurrencyManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side filtering and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch currencies with server-side filtering and pagination
  const { data: currenciesResponse, isLoading } = useQuery({
    queryKey: ['currencies', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/currency?${params.toString()}`);
      const result = response.data.data || {};
      const paginationData = result.pagination || {};
      
      return {
        data: result.data || [],
        pagination: {
          total: paginationData.total || 0,
          page: paginationData.page || 1,
          limit: paginationData.limit || 50,
          totalPages: paginationData.totalPages || 1
        }
      };
    },
  });

  const currencies = currenciesResponse?.data || [];
  const pagination = currenciesResponse?.pagination;

  // Create currency mutation
  const createCurrencyMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/currency', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      toast({
        title: 'Currency created',
        description: 'Currency has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingCurrency(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to create currency',
        variant: 'destructive',
      });
    },
  });

  // Update currency mutation
  const updateCurrencyMutation = useMutation({
    mutationFn: async (data: any) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(`/api/master-data/currency/${_id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      toast({
        title: 'Currency updated',
        description: 'Currency has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingCurrency(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to update currency',
        variant: 'destructive',
      });
    },
  });

  // Delete currency mutation
  const deleteCurrencyMutation = useMutation({
    mutationFn: async (currencyId: string) => {
      await axios.delete(`/api/master-data/currency/${currencyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      toast({
        title: 'Currency deleted',
        description: 'Currency has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete currency',
        variant: 'destructive',
      });
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['currencies'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/currency/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Sr No', 'Name', 'Code', 'Symbol', 'Decimal Places', 'Fractional Unit', 'Status', 'Created Date'];
          const rows = data.map((currency: any) => [
            currency.srNo || '',
            currency.name || '',
            currency.code || '',
            currency.symbol || '',
            currency.decimalPlaces !== undefined ? currency.decimalPlaces : '',
            currency.fractionalUnit || '',
            currency.status || '',
            currency.createdAt ? new Date(currency.createdAt).toLocaleDateString() : '',
          ]);
          const csvContent = buildCsv(headers, rows, { includeBom: true, quoteStrings: true });
          downloadText(
            csvContent,
            `currencies_export_${new Date().toISOString().split('T')[0]}.csv`,
            'text/csv;charset=utf-8;'
          );
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} currency records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No currencies match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export currencies',
        variant: 'destructive',
      });
    }
  };

  // Table columns
  const columns: TableColumn<Currency>[] = [
    {
      key: 'srNo',
      label: 'Sr No',
      sortable: true,
      width: 'w-20',
      render: (value) => value || '-',
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      width: 'w-20',
    },
    {
      key: 'name',
      label: 'Currency Name',
      sortable: true,
    },
    {
      key: 'symbol',
      label: 'Symbol',
      sortable: true,
      hideOnMobile: true,
      render: (value) => value || '-',
    },
    {
      key: 'decimalPlaces',
      label: 'Decimal Places',
      sortable: true,
      hideOnMobile: true,
      render: (value) => value !== undefined ? value : '-',
    },
    {
      key: 'fractionalUnit',
      label: 'Fractional Unit',
      sortable: true,
      hideOnMobile: true,
      render: (value) => value || '-',
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge className={value === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
          {value === 'ACTIVE' ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  // Filters
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: '', label: 'All Statuses' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
      ],
    },
  ];

  // Form sections
  const formSections: FormSection[] = [
    {
      title: 'Basic Information',
      description: 'Currency identification and display details',
      fields: [
        {
          name: 'srNo',
          label: 'Sr No',
          type: 'text',
          required: false,
          placeholder: 'Enter serial number (optional)',
        },
        {
          name: 'name',
          label: 'Currency Name',
          type: 'text',
          required: true,
          placeholder: 'Enter currency name (e.g., US Dollar)',
        },
        {
          name: 'code',
          label: 'Currency Code',
          type: 'text',
          required: true,
          placeholder: 'Enter 3-letter code (e.g., USD)',
        },
        {
          name: 'symbol',
          label: 'Currency Symbol',
          type: 'text',
          required: false,
          placeholder: 'Enter symbol (e.g., $) (optional)',
        },
        {
          name: 'decimalPlaces',
          label: 'Decimal Places',
          type: 'number',
          required: false,
          min: 0,
          max: 4,
          placeholder: 'Enter decimal places (0-4) (optional)',
        },
        {
          name: 'fractionalUnit',
          label: 'Currency Fractional Unit',
          type: 'text',
          required: false,
          placeholder: 'Enter fractional unit (e.g., Cents, Pence) (optional)',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          required: true,
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
          ],
        },
      ],
    },
  ];

  const handleAdd = () => {
    setEditingCurrency(null);
    setIsFormOpen(true);
  };

  const handleEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    setIsFormOpen(true);
  };

  const handleDelete = (currency: Currency) => {
    deleteCurrencyMutation.mutate(currency._id);
  };

  const handleFormSubmit = (data: any) => {
    // Transform form data
    const transformedData = {
      ...data,
      decimalPlaces: data.decimalPlaces !== '' && data.decimalPlaces !== undefined ? Number(data.decimalPlaces) : undefined,
      // Include optional fields only if they have values
      symbol: data.symbol || undefined,
      fractionalUnit: data.fractionalUnit || undefined,
      srNo: data.srNo || undefined,
    };

    if (editingCurrency) {
      updateCurrencyMutation.mutate({ ...transformedData, _id: editingCurrency._id });
    } else {
      createCurrencyMutation.mutate(transformedData);
    }
  };

  // Transform currency data for form
  const transformedEditingCurrency = editingCurrency ? {
    ...editingCurrency,
  } : null;

  return (
    <>
      <MasterDataTable
        title="Currency Management"
        description="Manage currencies"
        data={currencies}
        columns={columns}
        isLoading={isLoading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteConfirm
        onRefresh={handleRefresh}
        onExport={handleExport}
        filters={filters}
        searchPlaceholder="Search currencies by name, code, or symbol..."
        allowBulkOperations={false}
        allowImportExport={true}
        // Server-side filtering and pagination props
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearchTerm}
        onFiltersChange={setCurrentFilters}
        onSortChange={setSortConfig}
        currentSearch={searchTerm}
        currentFilters={currentFilters}
        currentSort={sortConfig}
      />

      <MasterDataForm
        title={editingCurrency ? 'Edit Currency' : 'Add New Currency'}
        description={
          editingCurrency
            ? 'Update currency information.'
            : 'Create a new currency.'
        }
        sections={formSections}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCurrency(null);
        }}
        onSubmit={handleFormSubmit}
        isLoading={createCurrencyMutation.isPending || updateCurrencyMutation.isPending}
        initialData={transformedEditingCurrency}
        mode={editingCurrency ? 'edit' : 'create'}
      />
    </>
  );
};

export default CurrencyManagement; 
